import Styles from './AutopilotFreemiumChannelSelectModal.module.scss'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { InformationCircleContained, TikTokBrand, TwitterBrand, YouTubeBrand } from 'blaze-ui/icons'
import { AutopilotProPill } from 'components/AutopilotUpgradeButtons/AutopilotProPill'
import { AutopilotProUpgradeButton } from 'components/AutopilotUpgradeButtons/AutopilotProUpgradeButton'
import { Text } from 'blaze-ui/Text/Text'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { HTMLAttributes, useEffect, useMemo } from 'react'
import { LimitedContentsCount } from 'components/LimitedContentsCount/LimitedContentsCount'
import CrossPostIcon from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/CrossPostIcon'
import { Formik, useField, useFormikContext } from 'formik'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { ContentsCountProps } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Types'
import { getTacticInfo } from 'components/TacticIntegrationList/utils'
import { Blog } from 'almanac-ui/icons/blaze/24/Blog'
import { DynamicHighDetailedMail } from 'blaze-ui/icons/40'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useAutopilotLimits } from 'hooks/useAutopilotLimits'
import { FormValues } from './types'
import { buildInitialValues, getUsedChannelsCount } from './utils'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const ContentsCount = (
  props: ContentsCountProps &
    HTMLAttributes<HTMLDivElement> & {
      customLabel?: string
      customCount?: number
      forcePlan?: PaymentPlanKey
      disableLockButtons?: boolean
    },
) => {
  const { name, forcePlan } = props
  const [checkedChannelsField, , checkedChannelsHelpers] = useField<string[]>('checked_channels')
  const { values, setFieldValue } = useFormikContext<FormValues>()
  const { maxChannels, disabledChannels, maxPostsPerChannel } = useAutopilotLimits({ forcePlan })

  const checkedChannels = checkedChannelsField.value
  const isChecked = checkedChannels.includes(name)
  const usedChannelsCount = getUsedChannelsCount(checkedChannels, disabledChannels)
  const isCheckboxDisabled = !isChecked && usedChannelsCount >= maxChannels

  const channelContents = values[name as keyof FormValues] as { type: string; count: number }[] | undefined
  const totalCount = useMemo(() => {
    if (channelContents && Array.isArray(channelContents)) {
      return channelContents.reduce((sum, item) => sum + item.count, 0)
    }
    return 0
  }, [channelContents])

  const handleCheckboxChange = (isSelected: boolean) => {
    const newCheckedChannels = isSelected
      ? [...checkedChannels, name]
      : checkedChannels.filter((channel) => channel !== name)

    checkedChannelsHelpers.setValue(newCheckedChannels)

    // Update the contents for the channel (set to 0 if the channel is unchecked, or max if checked)
    if (channelContents && Array.isArray(channelContents)) {
      let countValue = 0
      if (isSelected) {
        countValue = maxPostsPerChannel === Infinity ? 7 : maxPostsPerChannel
      }
      const updatedContents = channelContents.map((item) => ({
        ...item,
        count: countValue,
      }))
      setFieldValue(name, updatedContents)
    }
  }

  // uncheck if channel count goes to 0
  useEffect(() => {
    if (totalCount === 0 && isChecked) {
      checkedChannelsHelpers.setValue(checkedChannels.filter((channel) => channel !== name))
    }
  }, [totalCount, name, isChecked, checkedChannels, checkedChannelsHelpers])

  let tooltipLabel = ''
  if (name === 'crosspost_contents') {
    tooltipLabel = 'Upgrade to enable cross posts'
  } else if (isCheckboxDisabled) {
    tooltipLabel = 'To enable this channel, remove another first'
  }

  return (
    <Tooltip label={tooltipLabel} placement="top" hide={!tooltipLabel}>
      <div className={Styles.contentsCount}>
        {name !== 'crosspost_contents' && (
          <Checkbox size="lg" isSelected={isChecked} isDisabled={isCheckboxDisabled} onChange={handleCheckboxChange} />
        )}
        <LimitedContentsCount {...props} ignoreChannelLimit isDisabled={!isChecked} />
      </div>
    </Tooltip>
  )
}

const InfoTooltip = ({ message }: { message: string }) => {
  return (
    <Tooltip placement="top" label={message}>
      <div className={Styles.infoTooltip}>
        <InformationCircleContained size={15} color="var(--dark-40)" />
      </div>
    </Tooltip>
  )
}

const PostsWeekLabel = () => {
  return (
    <div className={Styles.postsWeekLabel}>
      <Text variant="label" color="var(--dark-60)">
        Posts/week
      </Text>
    </div>
  )
}

type Props = {
  onSubmit: (payload: FormValues) => void
  forcePlan?: PaymentPlanKey
  savedValues?: FormValues
  disableLockButtons?: boolean
}

export const AutopilotFreemiumChannelSelectModal = ({ onSubmit, forcePlan, savedValues }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { closeModal } = useModals()
  const { maxChannels, disabledChannels, maxPostsPerChannel } = useAutopilotLimits({ forcePlan })

  const { data: brandPlan } = useCurrentBrandPlan()

  const initialValues = useMemo(() => {
    return savedValues ?? buildInitialValues(brandPlan, maxChannels, maxPostsPerChannel)
  }, [brandPlan, maxChannels, maxPostsPerChannel, savedValues])

  const blogInfo = getTacticInfo('seo', brandPlan?.selected_blog_platform)
  const BlogIcon = blogInfo?.icon
  const emailInfo = getTacticInfo('email', undefined, brandPlan?.selected_email_platform)
  const EmailIcon = emailInfo?.icon
  const planName = forcePlan ? nameMap[forcePlan] : 'Free'

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {({ submitForm, values }) => {
        const usedChannelsCount = getUsedChannelsCount(values.checked_channels, disabledChannels)
        return (
          <Modal.Root onPressOutside={closeModal} bodyClassName={Styles.bodyClassName} size="lg">
            <Modal.Header
              title={
                <div className={Styles.headerTitle}>
                  <div className={Styles.headerTitleText}>
                    <Modal.BackButton onPress={closeModal} />
                    Select up to 3 channels for {planName} Plan
                  </div>
                  <Text color="var(--dark-60)">
                    {usedChannelsCount}/{maxChannels} selected
                  </Text>
                </div>
              }
              actions={
                <div className={Styles.headerActions}>
                  {!forcePlan && <AutopilotProUpgradeButton />}
                  <Button onPress={submitForm} aria-label={`Save and Continue to ${planName} Plan`}>
                    Save and Continue to {planName} Plan
                  </Button>
                </div>
              }
            />
            <Modal.Content withoutFooter={isSystemRedesignEnabled}>
              <div className={Styles.description}>
                {planName === 'Free' && (
                  <Text>
                    Select up to 3 channels. On {planName}, you can post once per week, per channel. Refer friends to
                    unlock up to 5 posts per week per channel.
                  </Text>
                )}
                {planName === 'Starter' && (
                  <Text>
                    Select up to 3 channels. On {planName}, posting is unlimited. Refer friends to earn more credits.
                  </Text>
                )}
              </div>
              <div className={Styles.content}>
                <div className={Styles.leftPanel}>
                  <div className={Styles.postSection}>
                    <Heading level={4}>Social Media</Heading>
                    {/* Cross posts - disabled in freemium */}
                    <div className={Styles.subSection}>
                      <div className={Styles.subSectionHeader}>
                        <Text variant="label" color="var(--dark-60)">
                          Cross posts
                        </Text>
                        <InfoTooltip message="Cross posts allow you to share the same post across multiple social media platforms at once. This feature is available on the Pro plan." />
                        <Tooltip placement="top" label="Upgrade to enable crosspost">
                          <AutopilotProPill />
                        </Tooltip>
                        <PostsWeekLabel />
                      </div>
                      <Tooltip placement="top" label="You have to upgrade to Pro to add cross posts">
                        <ContentsCount
                          name="crosspost_contents"
                          label="Cross"
                          isDisabled
                          isFullyGreyedOut
                          forcePlan={forcePlan}
                          icon={
                            <CrossPostIcon
                              providers={[
                                ExternalIntegrationEnum.Instagram,
                                ExternalIntegrationEnum.LinkedIn,
                                ExternalIntegrationEnum.Facebook,
                                ExternalIntegrationEnum.X,
                              ]}
                            />
                          }
                        />
                      </Tooltip>
                    </div>
                    {/* Individual Social Posts */}
                    <div className={Styles.subSection}>
                      <div className={Styles.subSectionHeader}>
                        <Text variant="label" color="var(--dark-60)">
                          Individual posts
                        </Text>
                        <InfoTooltip message="Unique posts created for each platform." />
                        <PostsWeekLabel />
                      </div>
                      <ContentsCount
                        name="facebook_contents"
                        label="Facebook"
                        forcePlan={forcePlan}
                        icon={<FacebookBrand size={30} />}
                      />
                      <ContentsCount
                        name="twitter_contents"
                        label="X/Twitter"
                        forcePlan={forcePlan}
                        icon={<TwitterBrand size={30} />}
                      />
                      <ContentsCount
                        name="linkedin_contents"
                        label="LinkedIn"
                        forcePlan={forcePlan}
                        icon={<LinkedInBrand size={30} />}
                      />
                      <ContentsCount
                        name="instagram_post_contents"
                        label="Instagram"
                        forcePlan={forcePlan}
                        icon={<InstagramBrand size={30} />}
                      />
                      <ContentsCount
                        name="instagram_story_contents"
                        label="Instagram"
                        forcePlan={forcePlan}
                        icon={<InstagramBrand size={30} />}
                      />
                    </div>
                  </div>
                </div>

                <div className={Styles.rightPanel}>
                  <div className={Styles.postSection}>
                    <div className={Styles.sectionTitle}>
                      <Heading level={4}>Short-form videos</Heading>
                      <PostsWeekLabel />
                    </div>
                    {/* Short form videos */}
                    <div className={Styles.subSection}>
                      <ContentsCount
                        name="tiktok_contents"
                        label="TikTok"
                        forcePlan={forcePlan}
                        icon={<TikTokBrand size={30} />}
                      />
                      <ContentsCount
                        name="instagram_reel_contents"
                        label="Instagram"
                        forcePlan={forcePlan}
                        icon={<InstagramBrand size={30} />}
                      />
                      <ContentsCount
                        name="youtube_contents"
                        label="YouTube"
                        forcePlan={forcePlan}
                        icon={<YouTubeBrand size={30} />}
                      />
                    </div>
                  </div>
                  <div className={Styles.postSection}>
                    <div className={Styles.sectionTitle}>
                      <Heading level={4}>Blog</Heading>
                      <PostsWeekLabel />
                    </div>
                    {/* Blog */}
                    <div className={Styles.subSection}>
                      <ContentsCount
                        name="seo_contents"
                        icon={BlogIcon ? <BlogIcon /> : <Blog />}
                        forcePlan={forcePlan}
                        customLabel={blogInfo?.name}
                      />
                    </div>
                  </div>
                  <div className={Styles.postSection}>
                    <div className={Styles.sectionTitle}>
                      <Heading level={4}>Email</Heading>
                      <PostsWeekLabel />
                    </div>
                    {/* Email */}
                    <div className={Styles.subSection}>
                      <ContentsCount
                        name="email_contents"
                        icon={EmailIcon ? <EmailIcon /> : <DynamicHighDetailedMail />}
                        forcePlan={forcePlan}
                        customLabel={emailInfo?.name}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Modal.Content>
          </Modal.Root>
        )
      }}
    </Formik>
  )
}
